import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WebinarListPresenter } from './webinar-list-presenter'
import { GetWebinarsSearchParams } from '../../lib/types'
import {
  WebinarListItem,
  webinarServices,
  WebinarsListResponse,
} from '../../services/webinar-service'

const DEFAULT_LIMIT = 10
const DEFAULT_SEARCH_PARAMS: GetWebinarsSearchParams = {
  limit: DEFAULT_LIMIT.toString(),
  offset: '0',
  keyword: '',
  sortBy: 'NEWEST_TO_OLDEST',
  status: undefined,
}
const DEFAULT_PAGINATION = {
  total: 0,
  limit: DEFAULT_LIMIT,
  count: 0,
  offset: 0,
}

const isWebinarsListResponse = (value: unknown): value is WebinarsListResponse =>
  typeof value === 'object' &&
  value !== null &&
  'webinars' in value &&
  'pagination' in value &&
  Array.isArray((value as WebinarsListResponse).webinars)

function WebinarListContainer() {
  const navigate = useNavigate()
  const [webinars, setWebinars] = useState<WebinarListItem[]>([])
  const [searchParams, setSearchParams] = useState<GetWebinarsSearchParams>(DEFAULT_SEARCH_PARAMS)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const fetchWebinars = async () => {
      const data = await webinarServices.getWebinars(searchParams)
      if (isWebinarsListResponse(data) && data.webinars.length > 0) {
        setWebinars(data.webinars)
        setPagination(data.pagination)
      } else {
        setWebinars([])
        setPagination(DEFAULT_PAGINATION)
      }
    }
    fetchWebinars()
  }, [currentPage, searchParams])

  const handleSearchParams = async ({ name, value }: { name: string; value: string }) => {
    const { limit } = DEFAULT_PAGINATION
    setSearchParams((prev) => ({
      ...prev,
      offset: '0',
      limit: limit.toString(),
      [name]: value,
    }))
    setCurrentPage(1)
    setPagination(DEFAULT_PAGINATION)
  }

  const resetSearchParams = async () => {
    setSearchParams(DEFAULT_SEARCH_PARAMS)
  }

  const handleCurrentPage = (page: number) => {
    const { limit } = DEFAULT_PAGINATION
    setCurrentPage(page)
    setSearchParams((prev) => ({
      ...prev,
      offset: `${(page - 1) * limit}`,
    }))
    setPagination((prev) => ({ ...prev, offset: (page - 1) * limit }))
  }

  const handleWebinarEdit = (id: number) => {
    navigate(`/webinars/${id}/edit`)
  }

  return (
    <WebinarListPresenter
      pagination={pagination}
      handleCurrentPage={handleCurrentPage}
      currentPage={currentPage}
      resetSearchParams={resetSearchParams}
      searchParams={searchParams}
      handleSearchParams={handleSearchParams}
      handleWebinarEdit={handleWebinarEdit}
      webinars={webinars}
    />
  )
}

export { WebinarListContainer }
